import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Market, PaginatedData } from '../@types/api';
import { QUERY_KEYS } from '../constants';
import { getData } from '../utils/api';
import useMarketsCount from './useMarketsCount';

const MARKETS_PAGE_LIMIT = 100;

const useMarketsInfiniteScroll = (eventId: string, activeGroup?: string | false, search?: string) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  const { data: totalCount } = useMarketsCount({
    eventId,
    marketTypeGroupId: activeGroup || undefined,
    search,
  });

  const {
    data: marketsData,
    fetchNextPage,
    hasNextPage,
    isPending: isLoading,
    isFetching,
  } = useInfiniteQuery<PaginatedData<Market>>({
    queryKey: [
      QUERY_KEYS.markets,
      {
        eventId,
        activeGroup,
        search,
        totalCount,
      },
    ],
    queryFn: ({ pageParam = 1 }) => {
      return getData<PaginatedData<Market>>('markets', {
        page: pageParam,
        limit: MARKETS_PAGE_LIMIT,
        marketTypeGroupId: activeGroup ? activeGroup : undefined,
        eventId,
        search: search?.trim() || undefined,
      });
    },
    getNextPageParam: (_, allPages) => {
      const fetchedCount = allPages.reduce((acc, page) => acc + page.items.length, 0);

      if (totalCount && fetchedCount < totalCount) {
        return allPages.length + 1;
      }
    },
    initialPageParam: 1,
  });

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetching || isFetchingNextPage) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          setIsFetchingNextPage(true);
          fetchNextPage().then(() => setIsFetchingNextPage(false));
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const markets = useMemo(() => marketsData?.pages.flatMap((page) => page.items), [marketsData]);

  return { markets, isLoading, isFetching, isFetchingNextPage, lastElementRef };
};

export default useMarketsInfiniteScroll;
